import { Mesh, MeshStandardMaterial, PlaneGeometry } from 'three';
import { createRandom } from '../../../utils/random.js';
import { createCanvas, finishTexture } from './theme.js';

/**
 * Revolution murals on the compound walls of Street 60.
 *
 * Every design here is drawn from scratch with the 2D canvas API — nothing is
 * copied from a photograph. The subjects are the motifs that filled Khartoum's
 * walls during the 2018–19 uprising, as described in the BBC's
 * "In pictures: The art fuelling Sudan's revolution" (May 2019): the Kandaka
 * with her raised hand, the dove, bullets turning into branches, broken chains,
 * the rababah player, the V sign, the blue/yellow/green of the 1956 flag, and
 * the faces of a country with more than a hundred dialects.
 *
 * A mural is a plane hung a couple of centimetres off the road-facing side of a
 * wall. Each design primes the whole panel first — a block of paint in its own
 * colour — because that is how the walls were actually painted, and because a
 * primed panel holds its contrast against the beige render at the fifteen or so
 * metres a driver sees it from. Art floating directly on the render disappears.
 */

/** Mural texture size. The aspect ratio drives the plane's shape. */
const TEXTURE_W = 1024;
const TEXTURE_H = 460;
const ASPECT = TEXTURE_W / TEXTURE_H;

const PALETTE = {
  cream: '#f2ead8',
  ink: '#171310',
  blue: '#1c4f9c',
  yellow: '#f0c22c',
  green: '#1d7a4b',
  red: '#c33028',
  brass: '#c9a13f',
};

/** Skin tones for the crowd, light enough to read against a primed panel. */
const TONES = ['#d8a273', '#b07a4a', '#8a5a34', '#e2b78c', '#6f4626', '#c08a5c'];

const ARABIC_FONT =
  '"Geeza Pro", "Al Nile", "Noto Naskh Arabic", "Segoe UI", Arial, sans-serif';
const LATIN_FONT = '"Arial Black", "Helvetica Neue", Arial, sans-serif';

/* ------------------------------------------------------------------ */
/* Painting helpers                                                    */
/* ------------------------------------------------------------------ */

/** Prime the whole panel. Every design starts with this. */
function prime(ctx, color) {
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, TEXTURE_W, TEXTURE_H);
}

/** Overspray: fine dots around a shape, the signature of a spray can. */
function speckle(ctx, random, color, x, y, radius, count = 60) {
  ctx.fillStyle = color;
  for (let i = 0; i < count; i += 1) {
    const angle = random() * Math.PI * 2;
    const distance = radius * (0.75 + random() * 0.5);
    ctx.globalAlpha = random.range(0.05, 0.3);
    ctx.beginPath();
    ctx.arc(
      x + Math.cos(angle) * distance,
      y + Math.sin(angle) * distance,
      random.range(0.8, 2.6),
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/** A few runs of paint down the wall. */
function drips(ctx, random, color, count = 5) {
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  for (let i = 0; i < count; i += 1) {
    const x = random.range(40, TEXTURE_W - 40);
    const top = random.range(TEXTURE_H * 0.25, TEXTURE_H * 0.7);
    ctx.globalAlpha = random.range(0.12, 0.32);
    ctx.lineWidth = random.range(1.6, 4);
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x + random.range(-3, 3), top + random.range(24, 96));
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/** Draw text, shrinking the font until it fits `maxWidth`. */
function fitText(ctx, text, x, y, { size, maxWidth, font, color, align = 'center' }) {
  let current = size;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  do {
    ctx.font = `700 ${current}px ${font}`;
    if (ctx.measureText(text).width <= maxWidth || current <= 12) break;
    current -= 2;
  } while (current > 12);
  ctx.fillText(text, x, y);
}

/** The Arabic line, right-to-left. */
function arabic(ctx, text, x, y, size, color, align = 'center', maxWidth) {
  ctx.save();
  ctx.direction = 'rtl';
  fitText(ctx, text, x, y, {
    size,
    maxWidth: maxWidth ?? TEXTURE_W * 0.46,
    font: ARABIC_FONT,
    color,
    align,
  });
  ctx.restore();
}

/**
 * A standing figure, drawn as a flat silhouette: head, tapering torso, a
 * skirt down to the feet, and one arm — raised in a fist for the protest
 * pieces, hanging for the rest.
 */
function standingFigure(ctx, { x, footY, height, color, armUp = false }) {
  const headR = height * 0.075;
  const headY = footY - height + headR;
  const shoulderY = headY + headR * 1.6;
  const hipY = footY - height * 0.4;
  const shoulderW = height * 0.125;

  ctx.fillStyle = color;

  ctx.beginPath();
  ctx.arc(x, headY, headR, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(x - shoulderW, shoulderY);
  ctx.lineTo(x + shoulderW, shoulderY);
  ctx.lineTo(x + shoulderW * 0.74, hipY);
  ctx.lineTo(x - shoulderW * 0.74, hipY);
  ctx.closePath();
  ctx.fill();

  // Tobe / skirt flaring to the ground.
  ctx.beginPath();
  ctx.moveTo(x - shoulderW * 0.74, hipY);
  ctx.lineTo(x + shoulderW * 0.74, hipY);
  ctx.lineTo(x + shoulderW * 1.02, footY);
  ctx.lineTo(x - shoulderW * 1.02, footY);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = color;
  ctx.lineWidth = height * 0.042;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x + shoulderW * 0.82, shoulderY + height * 0.02);
  if (armUp) {
    ctx.lineTo(x + shoulderW * 2.1, shoulderY - height * 0.28);
  } else {
    ctx.lineTo(x + shoulderW * 1.15, hipY);
  }
  ctx.stroke();

  if (armUp) {
    ctx.beginPath();
    ctx.arc(x + shoulderW * 2.1, shoulderY - height * 0.28, height * 0.036, 0, Math.PI * 2);
    ctx.fill();
  }
}

/* ------------------------------------------------------------------ */
/* The murals                                                          */
/* ------------------------------------------------------------------ */

/**
 * The Kandaka: a woman in a white tobe, arm up, finger raised — the image that
 * carried the sit-in. The silhouette is cut out of a gold sun disc.
 */
function kandaka(ctx, random) {
  prime(ctx, PALETTE.ink);

  const cx = TEXTURE_W * 0.27;
  const cy = TEXTURE_H * 0.52;
  const discR = TEXTURE_H * 0.46;

  ctx.fillStyle = PALETTE.yellow;
  ctx.beginPath();
  ctx.arc(cx, cy, discR, 0, Math.PI * 2);
  ctx.fill();
  speckle(ctx, random, PALETTE.yellow, cx, cy, discR * 1.06, 90);

  const headY = TEXTURE_H * 0.3;
  const headR = TEXTURE_H * 0.105;

  // Tobe: shoulders widening to the hem, cut out of the sun.
  ctx.fillStyle = PALETTE.ink;
  ctx.beginPath();
  ctx.moveTo(cx - 52, TEXTURE_H * 0.42);
  ctx.lineTo(cx - 118, TEXTURE_H * 0.99);
  ctx.lineTo(cx + 118, TEXTURE_H * 0.99);
  ctx.lineTo(cx + 52, TEXTURE_H * 0.42);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, headY, headR, 0, Math.PI * 2);
  ctx.fill();

  // Raised arm, hand held inside the disc so it never runs into the dark.
  ctx.strokeStyle = PALETTE.ink;
  ctx.lineWidth = TEXTURE_H * 0.062;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx + 44, TEXTURE_H * 0.47);
  ctx.lineTo(cx + 108, TEXTURE_H * 0.24);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx + 110, TEXTURE_H * 0.225, TEXTURE_H * 0.045, 0, Math.PI * 2);
  ctx.fill();
  // Two fingers up.
  ctx.fillRect(cx + 97, TEXTURE_H * 0.11, 8, TEXTURE_H * 0.09);
  ctx.fillRect(cx + 115, TEXTURE_H * 0.125, 8, TEXTURE_H * 0.08);

  // Gold earring.
  ctx.fillStyle = PALETTE.brass;
  ctx.beginPath();
  ctx.arc(cx + 32, TEXTURE_H * 0.335, TEXTURE_H * 0.024, 0, Math.PI * 2);
  ctx.fill();

  // One word per line. Three words strung across one line shrink to nothing at
  // the fifteen metres a driver sees this from; stacked, each stays huge.
  const tx = TEXTURE_W * 0.7;
  [
    ['حرية', PALETTE.yellow],
    ['سلام', PALETTE.cream],
    ['عدالة', PALETTE.yellow],
  ].forEach(([word, color], i) => {
    arabic(ctx, word, tx, TEXTURE_H * (0.2 + i * 0.235), 86, color, 'center', TEXTURE_W * 0.5);
  });
  fitText(ctx, 'FREEDOM · PEACE · JUSTICE', tx, TEXTURE_H * 0.91, {
    size: 30,
    maxWidth: TEXTURE_W * 0.5,
    font: LATIN_FONT,
    color: PALETTE.red,
  });

  drips(ctx, random, PALETTE.yellow, 4);
}

/** The dove at the vocational centre: peace, and the freedoms won so far. */
function dove(ctx, random) {
  prime(ctx, PALETTE.blue);

  const bx = TEXTURE_W * 0.3;
  const by = TEXTURE_H * 0.6;

  // Every part is drawn from a point inside the body, so the bird reads as one
  // animal rather than a pile of shapes.
  ctx.fillStyle = PALETTE.cream;

  // Wing, swept up and back.
  ctx.beginPath();
  ctx.moveTo(bx - 44, by - 34);
  ctx.quadraticCurveTo(bx - 30, by - 200, bx + 74, by - 214);
  ctx.quadraticCurveTo(bx + 96, by - 120, bx + 136, by - 16);
  ctx.quadraticCurveTo(bx + 40, by - 2, bx - 44, by - 34);
  ctx.fill();
  // Fold line, so the wing separates from the body at distance.
  ctx.strokeStyle = PALETTE.blue;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(bx - 44, by - 34);
  ctx.quadraticCurveTo(bx + 40, by - 2, bx + 136, by - 16);
  ctx.stroke();

  // Body.
  ctx.fillStyle = PALETTE.cream;
  ctx.beginPath();
  ctx.ellipse(bx, by, TEXTURE_H * 0.205, TEXTURE_H * 0.118, -0.1, 0, Math.PI * 2);
  ctx.fill();

  // Tail, rooted inside the body.
  ctx.beginPath();
  ctx.moveTo(bx - 78, by - 6);
  ctx.lineTo(bx - 212, by - 48);
  ctx.lineTo(bx - 212, by + 40);
  ctx.lineTo(bx - 78, by + 26);
  ctx.closePath();
  ctx.fill();

  // Head, overlapping the chest.
  ctx.beginPath();
  ctx.arc(bx + 96, by - 56, TEXTURE_H * 0.086, 0, Math.PI * 2);
  ctx.fill();

  // Beak.
  ctx.beginPath();
  ctx.moveTo(bx + 128, by - 70);
  ctx.lineTo(bx + 176, by - 54);
  ctx.lineTo(bx + 128, by - 38);
  ctx.closePath();
  ctx.fill();

  // Olive branch, in yellow: green would sink into the blue.
  ctx.strokeStyle = PALETTE.yellow;
  ctx.lineWidth = 8;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(bx + 168, by - 56);
  ctx.quadraticCurveTo(bx + 244, by - 26, bx + 286, by + 66);
  ctx.stroke();
  ctx.fillStyle = PALETTE.yellow;
  for (let i = 0; i < 4; i += 1) {
    const t = 0.25 + i * 0.18;
    const x = bx + 168 + (286 - 168) * t;
    const y = by - 56 + (66 + 56) * t * t;
    ctx.beginPath();
    ctx.ellipse(x, y - 16, 24, 11, -0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x + 11, y + 16, 24, 11, 0.7, 0, Math.PI * 2);
    ctx.fill();
  }

  speckle(ctx, random, PALETTE.cream, bx + 40, by - 40, 210, 70);

  const tx = TEXTURE_W * 0.79;
  arabic(ctx, 'سلام', tx, TEXTURE_H * 0.28, 96, PALETTE.cream, 'center', TEXTURE_W * 0.34);
  fitText(ctx, 'SALAM', tx, TEXTURE_H * 0.55, {
    size: 58,
    maxWidth: TEXTURE_W * 0.36,
    font: LATIN_FONT,
    color: PALETTE.yellow,
  });
  fitText(ctx, 'PEACE', tx, TEXTURE_H * 0.75, {
    size: 32,
    maxWidth: TEXTURE_W * 0.36,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });

  drips(ctx, random, PALETTE.cream, 3);
}

/** Bullets that sprout instead of landing — the call for a peaceful transfer. */
function bullets(ctx, random) {
  prime(ctx, PALETTE.ink);

  const baseY = TEXTURE_H * 0.88;
  const count = 6;

  for (let i = 0; i < count; i += 1) {
    const x = TEXTURE_W * 0.08 + i * TEXTURE_W * 0.072;
    const height = TEXTURE_H * random.range(0.26, 0.36);

    ctx.fillStyle = PALETTE.brass;
    ctx.beginPath();
    ctx.roundRect(x - 11, baseY - height, 22, height, 9);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x - 11, baseY - height + 4);
    ctx.quadraticCurveTo(x, baseY - height - 46, x + 11, baseY - height + 4);
    ctx.closePath();
    ctx.fill();

    // Stem and leaves growing out of the tip.
    const tipY = baseY - height - 40;
    ctx.strokeStyle = PALETTE.green;
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, tipY);
    ctx.quadraticCurveTo(x + 14, tipY - 60, x + 4, tipY - 110);
    ctx.stroke();

    ctx.fillStyle = PALETTE.green;
    ctx.beginPath();
    ctx.ellipse(x + 26, tipY - 62, 26, 11, -0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x - 20, tipY - 92, 24, 10, 0.6, 0, Math.PI * 2);
    ctx.fill();

    if (i % 2 === 0) {
      ctx.fillStyle = PALETTE.yellow;
      ctx.beginPath();
      ctx.arc(x + 4, tipY - 118, 13, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  speckle(ctx, random, PALETTE.green, TEXTURE_W * 0.28, TEXTURE_H * 0.4, 200, 80);

  const tx = TEXTURE_W * 0.73;
  fitText(ctx, 'BULLETS INTO', tx, TEXTURE_H * 0.26, {
    size: 52,
    maxWidth: TEXTURE_W * 0.52,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });
  fitText(ctx, 'BRANCHES', tx, TEXTURE_H * 0.44, {
    size: 52,
    maxWidth: TEXTURE_W * 0.52,
    font: LATIN_FONT,
    color: PALETTE.yellow,
  });
  arabic(ctx, 'الرصاص يصير شجر', tx, TEXTURE_H * 0.62, 44, PALETTE.green, 'center', TEXTURE_W * 0.52);
  fitText(ctx, 'A PEACEFUL TRANSFER OF POWER', tx, TEXTURE_H * 0.8, {
    size: 24,
    maxWidth: TEXTURE_W * 0.52,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });

  drips(ctx, random, PALETTE.cream, 3);
}

/** A chain snapped in two: "the people broke the chains that kept them silent". */
function chains(ctx, random) {
  prime(ctx, PALETTE.green);

  const y = TEXTURE_H * 0.32;
  const linkW = 92;
  const linkH = 54;
  const gapIndex = 4;

  ctx.strokeStyle = PALETTE.cream;
  ctx.lineWidth = 16;

  for (let i = 0; i < 9; i += 1) {
    if (i === gapIndex) continue;
    const x = TEXTURE_W * 0.07 + i * linkW * 0.86;
    // Alternate the link orientation so the chain reads as interlocking.
    const vertical = i % 2 === 0;
    ctx.beginPath();
    ctx.ellipse(
      x,
      y,
      vertical ? linkH * 0.5 : linkW * 0.5,
      vertical ? linkH : linkH * 0.62,
      vertical ? Math.PI / 2 : 0,
      0,
      Math.PI * 2,
    );
    ctx.stroke();
  }

  // The broken link, thrown clear.
  const breakX = TEXTURE_W * 0.07 + gapIndex * linkW * 0.86;
  ctx.save();
  ctx.translate(breakX + 42, y - 98);
  ctx.rotate(-0.5);
  ctx.beginPath();
  ctx.arc(0, 0, linkH * 0.72, -0.5, Math.PI * 1.2);
  ctx.stroke();
  ctx.restore();

  // Snap sparks.
  ctx.strokeStyle = PALETTE.yellow;
  ctx.lineWidth = 8;
  for (let i = 0; i < 5; i += 1) {
    const angle = -1.1 + i * 0.42;
    ctx.beginPath();
    ctx.moveTo(breakX + Math.cos(angle) * 26, y + Math.sin(angle) * 26);
    ctx.lineTo(breakX + Math.cos(angle) * 64, y + Math.sin(angle) * 64);
    ctx.stroke();
  }

  fitText(ctx, 'WE BROKE THE CHAINS', TEXTURE_W * 0.5, TEXTURE_H * 0.68, {
    size: 66,
    maxWidth: TEXTURE_W * 0.86,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });
  arabic(ctx, 'كسرنا السلاسل', TEXTURE_W * 0.5, TEXTURE_H * 0.88, 40, PALETTE.yellow, 'center', TEXTURE_W * 0.6);

  drips(ctx, random, PALETTE.cream, 4);
}

/** The rababah player: one instrument across every group in the country. */
function rababah(ctx, random) {
  prime(ctx, PALETTE.ink);

  // A gold panel for the player to sit in, so the figure stays visible.
  ctx.fillStyle = PALETTE.yellow;
  ctx.beginPath();
  ctx.roundRect(TEXTURE_W * 0.03, TEXTURE_H * 0.07, TEXTURE_W * 0.41, TEXTURE_H * 0.88, 28);
  ctx.fill();

  const cx = TEXTURE_W * 0.225;
  const headY = TEXTURE_H * 0.26;

  ctx.fillStyle = PALETTE.ink;
  // Seated figure: head, torso, crossed legs.
  ctx.beginPath();
  ctx.arc(cx, headY, TEXTURE_H * 0.115, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - 66, TEXTURE_H * 0.42);
  ctx.quadraticCurveTo(cx, TEXTURE_H * 0.33, cx + 66, TEXTURE_H * 0.42);
  ctx.lineTo(cx + 88, TEXTURE_H * 0.74);
  ctx.lineTo(cx - 88, TEXTURE_H * 0.74);
  ctx.closePath();
  ctx.fill();
  // Crossed legs, a low wide mass under the torso.
  ctx.beginPath();
  ctx.ellipse(cx, TEXTURE_H * 0.79, 104, 34, 0, 0, Math.PI * 2);
  ctx.fill();

  // Rababah held clear of the chest: a gourd body with a long neck rising
  // up-right, and the bow crossing the strings. It sits away from the torso
  // and carries a cream core, because an ink instrument against an ink figure
  // reads as one shapeless mass.
  const gourdX = cx + 78;
  const gourdY = TEXTURE_H * 0.62;
  const neckX = cx + 186;
  const neckY = TEXTURE_H * 0.28;

  ctx.strokeStyle = PALETTE.ink;
  ctx.lineWidth = 17;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(gourdX, gourdY);
  ctx.lineTo(neckX, neckY);
  ctx.stroke();
  // Cream core down the neck, so it separates from the figure behind it.
  ctx.strokeStyle = PALETTE.cream;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(gourdX, gourdY);
  ctx.lineTo(neckX - 4, neckY + 10);
  ctx.stroke();

  ctx.fillStyle = PALETTE.red;
  ctx.beginPath();
  ctx.arc(gourdX, gourdY, 42, 0, Math.PI * 2);
  ctx.fill();

  // The arm reaching out to hold it.
  ctx.strokeStyle = PALETTE.ink;
  ctx.lineWidth = 16;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx + 40, TEXTURE_H * 0.46);
  ctx.lineTo(gourdX - 6, gourdY - 18);
  ctx.stroke();

  // Bow: a short arc across the strings, in the other hand.
  ctx.strokeStyle = PALETTE.ink;
  ctx.lineWidth = 11;
  ctx.beginPath();
  ctx.moveTo(cx - 40, TEXTURE_H * 0.46);
  ctx.quadraticCurveTo(cx + 26, TEXTURE_H * 0.54, cx + 66, TEXTURE_H * 0.7);
  ctx.stroke();
  ctx.fillStyle = PALETTE.ink;
  ctx.beginPath();
  ctx.arc(cx - 40, TEXTURE_H * 0.46, 16, 0, Math.PI * 2);
  ctx.fill();

  speckle(ctx, random, PALETTE.ink, cx, TEXTURE_H * 0.55, 160, 50);

  const tx = TEXTURE_W * 0.745;
  fitText(ctx, 'ONE STREET', tx, TEXTURE_H * 0.28, {
    size: 58,
    maxWidth: TEXTURE_W * 0.44,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });
  fitText(ctx, 'ONE VOICE', tx, TEXTURE_H * 0.5, {
    size: 58,
    maxWidth: TEXTURE_W * 0.44,
    font: LATIN_FONT,
    color: PALETTE.yellow,
  });
  arabic(ctx, 'صوت واحد', tx, TEXTURE_H * 0.73, 44, PALETTE.cream, 'center', TEXTURE_W * 0.42);
  fitText(ctx, 'RABABAH · EVERY TRIBE, ONE SONG', tx, TEXTURE_H * 0.9, {
    size: 20,
    maxWidth: TEXTURE_W * 0.44,
    font: LATIN_FONT,
    color: PALETTE.green,
  });

  drips(ctx, random, PALETTE.yellow, 3);
}

/** The raised hand: two fingers up, "tasgot bas" — just fall. */
function victory(ctx, random) {
  prime(ctx, PALETTE.ink);

  const cx = TEXTURE_W * 0.26;
  const cy = TEXTURE_H * 0.6;

  ctx.fillStyle = PALETTE.yellow;
  // Palm.
  ctx.beginPath();
  ctx.roundRect(cx - 64, cy - 40, 128, 152, 34);
  ctx.fill();
  // Two fingers.
  ctx.beginPath();
  ctx.roundRect(cx - 54, cy - 172, 42, 154, 21);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(cx + 14, cy - 194, 42, 176, 21);
  ctx.fill();
  // Thumb.
  ctx.beginPath();
  ctx.roundRect(cx + 54, cy - 20, 56, 36, 18);
  ctx.fill();

  speckle(ctx, random, PALETTE.yellow, cx, cy - 30, 210, 110);

  const tx = TEXTURE_W * 0.71;
  arabic(ctx, 'تسقط بس', tx, TEXTURE_H * 0.3, 88, PALETTE.cream, 'center', TEXTURE_W * 0.5);
  fitText(ctx, 'TASGOT BAS', tx, TEXTURE_H * 0.56, {
    size: 54,
    maxWidth: TEXTURE_W * 0.5,
    font: LATIN_FONT,
    color: PALETTE.red,
  });
  fitText(ctx, 'JUST FALL', tx, TEXTURE_H * 0.76, {
    size: 34,
    maxWidth: TEXTURE_W * 0.5,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });

  drips(ctx, random, PALETTE.yellow, 4);
}

/** The 1956 flag — blue, yellow, green — with a family standing in front. */
function oldFlag(ctx, random) {
  const band = TEXTURE_H / 3;
  [PALETTE.blue, PALETTE.yellow, PALETTE.green].forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.fillRect(0, i * band, TEXTURE_W, band + 1);
  });

  // A family on the left, silhouetted across the flag: man, woman, child,
  // each with a hand up. The child stands at the mother's hem.
  standingFigure(ctx, {
    x: TEXTURE_W * 0.13,
    footY: TEXTURE_H * 0.99,
    height: TEXTURE_H * 0.72,
    color: PALETTE.ink,
    armUp: true,
  });
  standingFigure(ctx, {
    x: TEXTURE_W * 0.26,
    footY: TEXTURE_H * 0.99,
    height: TEXTURE_H * 0.66,
    color: PALETTE.ink,
    armUp: true,
  });
  standingFigure(ctx, {
    x: TEXTURE_W * 0.355,
    footY: TEXTURE_H * 0.99,
    height: TEXTURE_H * 0.4,
    color: PALETTE.ink,
    armUp: true,
  });

  // "1956" sits alone on the blue band — cream on blue is the only pairing in
  // this flag that holds contrast, so the number goes there and everything
  // else falls into the bands below it.
  const tx = TEXTURE_W * 0.7;
  fitText(ctx, '1956', tx, TEXTURE_H * 0.16, {
    size: 124,
    maxWidth: TEXTURE_W * 0.5,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });
  fitText(ctx, 'THE FLAG THEY', tx, TEXTURE_H * 0.42, {
    size: 44,
    maxWidth: TEXTURE_W * 0.5,
    font: LATIN_FONT,
    color: PALETTE.ink,
  });
  fitText(ctx, 'TOOK DOWN', tx, TEXTURE_H * 0.56, {
    size: 44,
    maxWidth: TEXTURE_W * 0.5,
    font: LATIN_FONT,
    color: PALETTE.ink,
  });
  arabic(ctx, 'علم الاستقلال', tx, TEXTURE_H * 0.72, 42, PALETTE.cream, 'center', TEXTURE_W * 0.5);
  fitText(ctx, 'INDEPENDENCE · 1 JANUARY 1956', tx, TEXTURE_H * 0.87, {
    size: 26,
    maxWidth: TEXTURE_W * 0.5,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });

  drips(ctx, random, PALETTE.cream, 4);
}

/** Faces of the sit-in: many groups, more than a hundred dialects. */
function diversity(ctx, random) {
  prime(ctx, PALETTE.ink);

  const count = 6;
  const spacing = TEXTURE_W / (count + 1);
  const bottom = TEXTURE_H * 0.92;
  // Slight height variation so the row reads as a crowd, not a picket fence.
  const drop = [0, 12, -6, 8, -10, 4];

  for (let i = 0; i < count; i += 1) {
    const x = spacing * (i + 1);
    const headR = TEXTURE_H * 0.135;
    const headY = TEXTURE_H * 0.33 + drop[i];
    const shoulderW = TEXTURE_H * 0.2;
    const collarY = headY + headR * 0.62;
    const corner = shoulderW * 0.62;

    ctx.fillStyle = TONES[i % TONES.length];

    ctx.beginPath();
    ctx.arc(x, headY, headR, 0, Math.PI * 2);
    ctx.fill();

    // Shoulders as a slab with rounded top corners. A half-ellipse here made
    // every figure look like an urn.
    ctx.beginPath();
    ctx.moveTo(x - shoulderW, bottom);
    ctx.lineTo(x - shoulderW, collarY + corner);
    ctx.quadraticCurveTo(x - shoulderW, collarY, x - shoulderW + corner, collarY);
    ctx.lineTo(x + shoulderW - corner, collarY);
    ctx.quadraticCurveTo(x + shoulderW, collarY, x + shoulderW, collarY + corner);
    ctx.lineTo(x + shoulderW, bottom);
    ctx.closePath();
    ctx.fill();
  }

  // The old-flag rule across the bottom, the thing they are all standing on.
  const stripeW = TEXTURE_W / 3;
  [PALETTE.blue, PALETTE.yellow, PALETTE.green].forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.fillRect(i * stripeW, TEXTURE_H * 0.88, stripeW, TEXTURE_H * 0.12);
  });

  fitText(ctx, 'MANY TONGUES · ONE STREET', TEXTURE_W * 0.5, TEXTURE_H * 0.115, {
    size: 42,
    maxWidth: TEXTURE_W * 0.86,
    font: LATIN_FONT,
    color: PALETTE.cream,
  });

  drips(ctx, random, PALETTE.cream, 3);
}

const DESIGNS = [
  { id: 'kandaka', paint: kandaka },
  { id: 'dove', paint: dove },
  { id: 'bullets', paint: bullets },
  { id: 'chains', paint: chains },
  { id: 'rababah', paint: rababah },
  { id: 'victory', paint: victory },
  { id: 'oldFlag', paint: oldFlag },
  { id: 'diversity', paint: diversity },
];

/* ------------------------------------------------------------------ */
/* Placement                                                           */
/* ------------------------------------------------------------------ */

/**
 * Build one texture per design, then hang them on walls spread along the road.
 *
 * Textures are shared between placements: eight painted murals repeat along a
 * five-kilometre street, the way a movement's slogans actually did.
 */
export function buildGraffiti(level, route, walls) {
  if (!walls?.length) return { count: 0, designs: 0 };

  const random = createRandom(20419);

  // Pick walls with a minimum gap so the street reads as painted here and
  // there, rather than wallpapered.
  const ordered = [...walls].sort((a, b) => a.s - b.s);
  const chosen = [];
  let lastS = -Infinity;
  for (const wall of ordered) {
    if (wall.s - lastS < 165) continue;
    if (random() < 0.25) continue;
    chosen.push(wall);
    lastS = wall.s;
  }

  const materials = DESIGNS.map((design) => {
    const canvas = createCanvas(TEXTURE_W, TEXTURE_H);
    const ctx = canvas.getContext('2d');
    design.paint(ctx, createRandom(design.id.length * 977));
    return {
      id: design.id,
      // Transparent so the plane's own outline never cuts into the wall;
      // depthWrite stays off because the plane never occludes anything.
      base: new MeshStandardMaterial({
        map: finishTexture(canvas, 4),
        roughness: 0.94,
        metalness: 0.015,
        transparent: true,
        depthWrite: false,
      }),
    };
  });

  const geometry = new PlaneGeometry(1, 1);
  const placements = [];

  for (const wall of chosen) {
    // A mural is painted at one size wherever it goes. The wall builder ends
    // every run with a short remainder segment, and a plane squeezed into one
    // of those reads as a squashed poster — so short segments are skipped
    // rather than scaled down to fit.
    const height = Math.min(2.0, Math.max(1.6, wall.height - 0.55));
    const width = height * ASPECT;
    if (wall.length < width + 0.6) continue;

    const design = materials[placements.length % materials.length];
    const frame = route.offsetFrame(wall.s, wall.lateral - wall.side * 0.18);
    const mesh = new Mesh(geometry, design.base);
    mesh.scale.set(width, height, 1);
    mesh.position.set(frame.position.x, 0.32 + height / 2, frame.position.z);
    mesh.rotation.y = frame.yaw + (wall.side > 0 ? -Math.PI / 2 : Math.PI / 2);
    mesh.receiveShadow = true;
    mesh.name = `graffiti-${design.id}-${placements.length}`;
    level.root.add(mesh);
    placements.push({
      design: design.id,
      s: wall.s,
      side: wall.side,
      width,
      height,
    });
  }

  return { count: placements.length, designs: materials.length, placements };
}
